import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import RequestSwapDialog from './RequestSwapDialog';
import {
  addDaysIso,
  apiError,
  AvailabilityKind,
  formatWallTime,
  isoToday,
  startOfWeekIso,
  weekdayKey,
  weekdayKeyForIndex,
  type AvailabilityWindow,
  type CreateAvailabilityInput,
  type MyRoster,
  type ScheduledShift,
} from './schedulingLib';

type AvailForm = 'weekly' | 'dated';

/**
 * The staff view — `SchedulingController.myRoster`'s own comment: "my
 * published shifts, and the open ones I could take." DRAFT shifts never
 * appear here, and the employee behind the login is resolved server-side
 * (`requireOwnEmployee`), never sent as a parameter, so there is no id here
 * for a client to change into somebody else's week.
 *
 * Held by every floor role (`scheduling.view_own` — WAITER, CASHIER,
 * HEAD_WAITER, and the two manager roles alongside their own `.manage`), so
 * this is the tab that gives the rota's largest audience an actual door into
 * the epic, not only the people building it.
 */
export default function MyScheduleTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canRequestSwap = can('scheduling.swap_request');
  const confirm = useConfirm().confirm;

  const [weekStart, setWeekStart] = useState(() => startOfWeekIso(isoToday()));
  const from = weekStart;
  const to = addDaysIso(weekStart, 6);

  const [roster, setRoster] = useState<MyRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [swapShift, setSwapShift] = useState<ScheduledShift | null>(null);

  const [availability, setAvailability] = useState<AvailabilityWindow[]>([]);
  const [availLoading, setAvailLoading] = useState(true);
  const [availError, setAvailError] = useState<string | null>(null);
  const [availOpen, setAvailOpen] = useState(false);

  const [kind, setKind] = useState<AvailabilityKind>(AvailabilityKind.UNAVAILABLE);
  const [form, setForm] = useState<AvailForm>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [savingAvail, setSavingAvail] = useState(false);

  const loadRoster = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<MyRoster>('/scheduling/me', { params: { from, to } })
      .then(({ data }) => setRoster(data))
      .catch((e) => setError(apiError(e, t('scheduling.mine.loadFailed'))))
      .finally(() => setLoading(false));
  }, [from, to, t]);

  const loadAvailability = useCallback(() => {
    setAvailLoading(true);
    setAvailError(null);
    api
      .get<AvailabilityWindow[]>('/scheduling/me/availability')
      .then(({ data }) => setAvailability(data ?? []))
      .catch((e) => setAvailError(apiError(e, t('scheduling.availability.loadFailed'))))
      .finally(() => setAvailLoading(false));
  }, [t]);

  useEffect(loadRoster, [loadRoster]);
  useEffect(loadAvailability, [loadAvailability]);

  const claim = async (shift: ScheduledShift) => {
    setClaimingId(shift.id);
    try {
      await api.post(`/scheduling/shifts/${shift.id}/claim`);
      toast.success(t('scheduling.mine.claimed'));
    } catch (e) {
      toast.error(apiError(e, t('scheduling.mine.claimFailed')));
    } finally {
      setClaimingId(null);
      loadRoster();
    }
  };

  const openAdd = () => {
    setKind(AvailabilityKind.UNAVAILABLE);
    setForm('weekly');
    setDayOfWeek('1');
    setStartTime('');
    setEndTime('');
    setStartsAt('');
    setEndsAt('');
    setReason('');
    setAvailOpen(true);
  };

  const submitAvailability = async () => {
    setSavingAvail(true);
    const payload: CreateAvailabilityInput =
      form === 'weekly'
        ? {
            kind,
            dayOfWeek: Number(dayOfWeek),
            startTime: startTime || undefined,
            endTime: endTime || undefined,
            reason: reason.trim() || undefined,
          }
        : {
            kind,
            startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
            endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
            reason: reason.trim() || undefined,
          };
    try {
      await api.post('/scheduling/me/availability', payload);
      toast.success(t('scheduling.availability.added'));
      setAvailOpen(false);
      loadAvailability();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.availability.saveFailed')));
    } finally {
      setSavingAvail(false);
    }
  };

  const removeAvailability = async (w: AvailabilityWindow) => {
    const ok = await confirm({
      title: t('scheduling.availability.removeTitle'),
      confirmLabel: t('common.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/scheduling/me/availability/${w.id}`);
      loadAvailability();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.availability.removeFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <h2 className="text-sm font-semibold">{t('scheduling.mine.weekTitle')}</h2>
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addDaysIso(w, -7))}>
              {t('common.previous')}
            </Button>
            <span className="px-1 text-sm font-medium tabular-nums">
              {from} – {to}
            </span>
            <Button variant="outline" size="sm" onClick={() => setWeekStart((w) => addDaysIso(w, 7))}>
              {t('common.next')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={loadRoster} />
      ) : !roster || roster.shifts.length === 0 ? (
        <EmptyState title={t('scheduling.mine.empty')} hint={t('scheduling.mine.emptyHint')} />
      ) : (
        <div className="space-y-2">
          {roster.shifts.map((shift) => {
            const canSwap = canRequestSwap && new Date(shift.startsAt) > new Date();
            return (
              <Card key={shift.id}>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <div>
                    <p className="text-sm font-semibold">{t(weekdayKey(shift.businessDate))}</p>
                    <p className="text-sm tabular-nums">
                      {formatWallTime(shift.startsAt, shift.timezone, i18n.language)}–
                      {formatWallTime(shift.endsAt, shift.timezone, i18n.language)}
                      {shift.role && <span className="text-ink-muted"> · {shift.role.name}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={toneForStatus(shift.status)}>
                      {t(`scheduling.scheduledShiftStatus.${shift.status}`)}
                    </Badge>
                    {canSwap && (
                      <Button variant="outline" size="sm" onClick={() => setSwapShift(shift)}>
                        {t('scheduling.mine.requestSwap')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && !error && roster && roster.openShifts.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold">{t('scheduling.mine.openShifts')}</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {roster.openShifts.map((shift) => (
              <Card key={shift.id}>
                <CardContent className="space-y-1 p-3">
                  <p className="text-sm font-medium tabular-nums">{shift.businessDate}</p>
                  <p className="text-sm tabular-nums">
                    {formatWallTime(shift.startsAt, shift.timezone, i18n.language)}–
                    {formatWallTime(shift.endsAt, shift.timezone, i18n.language)}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {shift.role?.name ?? t('scheduling.shift.noRole')}
                  </p>
                  <Button
                    size="sm"
                    disabled={claimingId === shift.id}
                    onClick={() => void claim(shift)}
                  >
                    {t('scheduling.mine.claim')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{t('scheduling.availability.title')}</h2>
            <Button size="sm" variant="outline" onClick={openAdd}>
              <Plus className="h-4 w-4" />
              {t('scheduling.availability.addWindow')}
            </Button>
          </div>

          {availLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : availError ? (
            <ErrorState message={availError} onRetry={loadAvailability} />
          ) : availability.length === 0 ? (
            <EmptyState
              title={t('scheduling.availability.empty')}
              hint={t('scheduling.mine.availabilityEmptyHint')}
            />
          ) : (
            <ul className="divide-y">
              {availability.map((w) => (
                <li key={w.id} className="flex items-center justify-between gap-2 py-2.5 text-sm">
                  <div>
                    <Badge tone={w.kind === AvailabilityKind.AVAILABLE ? 'success' : 'warning'}>
                      {t(`scheduling.availabilityKind.${w.kind}`)}
                    </Badge>{' '}
                    {w.startsAt && w.endsAt ? (
                      <span>
                        {new Date(w.startsAt).toLocaleDateString(i18n.language)} –{' '}
                        {new Date(w.endsAt).toLocaleDateString(i18n.language)}
                      </span>
                    ) : w.dayOfWeek !== null ? (
                      <span>
                        {t(weekdayKeyForIndex(w.dayOfWeek))}
                        {w.startTime && w.endTime
                          ? ` · ${w.startTime}–${w.endTime}`
                          : ` · ${t('scheduling.availability.allDay')}`}
                      </span>
                    ) : (
                      '—'
                    )}
                    {w.reason && <span className="text-ink-muted"> · {w.reason}</span>}
                  </div>
                  <button
                    aria-label={t('common.remove')}
                    onClick={() => void removeAvailability(w)}
                    className="rounded p-1 text-ink-muted hover:bg-hover hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={availOpen}
        onClose={() => setAvailOpen(false)}
        title={t('scheduling.availability.addWindow')}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAvailOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={savingAvail} onClick={() => void submitAvailability()}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Field label={t('scheduling.availability.kind')}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as AvailabilityKind)}>
              <option value={AvailabilityKind.UNAVAILABLE}>
                {t('scheduling.availabilityKind.UNAVAILABLE')}
              </option>
              <option value={AvailabilityKind.AVAILABLE}>
                {t('scheduling.availabilityKind.AVAILABLE')}
              </option>
            </Select>
          </Field>
          <Field label={t('scheduling.availability.form')}>
            <Select value={form} onChange={(e) => setForm(e.target.value as AvailForm)}>
              <option value="weekly">{t('scheduling.availability.weeklyForm')}</option>
              <option value="dated">{t('scheduling.availability.datedForm')}</option>
            </Select>
          </Field>

          {form === 'weekly' ? (
            <>
              <Field label={t('scheduling.availability.dayOfWeek')}>
                <Select value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    <option key={d} value={d}>
                      {t(weekdayKeyForIndex(d))}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('scheduling.shift.startTime')} hint={t('scheduling.availability.allDayHint')}>
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </Field>
                <Field label={t('scheduling.shift.endTime')}>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </Field>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('scheduling.availability.from')}>
                <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </Field>
              <Field label={t('scheduling.availability.to')}>
                <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </Field>
            </div>
          )}

          <Field label={t('scheduling.availability.reason')}>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </div>
      </Dialog>

      {swapShift && (
        <RequestSwapDialog
          shift={swapShift}
          onClose={() => setSwapShift(null)}
          onSent={() => setSwapShift(null)}
        />
      )}
    </div>
  );
}
